import { useState, useEffect, FormEvent } from 'react'
import { useCreateFolder, useUpdateFolder } from '../../hooks/useFolders'
import { Modal, Button, Input } from '../common'
import { Folder } from '../../types/folder'
import styles from './CreateFolderModal.module.css'

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  /** When provided, the modal edits this folder instead of creating a new one. */
  folder?: Folder | null
}

const FOLDER_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#ef4444', // red
  '#f59e0b', // amber
  '#10b981', // green
  '#06b6d4', // cyan
  '#3b82f6', // blue
]

export function CreateFolderModal({ isOpen, onClose, folder }: CreateFolderModalProps) {
  const isEdit = !!folder
  const [name, setName] = useState('')
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0])
  const createFolder = useCreateFolder()
  const updateFolder = useUpdateFolder()
  const mutation = isEdit ? updateFolder : createFolder

  // Sync form fields when (re)opening for a specific folder.
  useEffect(() => {
    if (!isOpen) return
    setName(folder?.name ?? '')
    setSelectedColor(folder?.color ?? FOLDER_COLORS[0])
  }, [isOpen, folder])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    try {
      if (isEdit && folder) {
        await updateFolder.mutateAsync({ id: folder.id, updates: { name: name.trim(), color: selectedColor } })
      } else {
        await createFolder.mutateAsync({ name: name.trim(), color: selectedColor })
      }
      setName('')
      setSelectedColor(FOLDER_COLORS[0])
      onClose()
    } catch (error) {
      console.error('Failed to save folder:', error)
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose} title={isEdit ? 'Edit Folder' : 'Create Folder'} size="sm">
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="folderName" className={styles.label}>
            Folder Name
          </label>
          <Input
            id="folderName"
            block
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter folder name"
            autoFocus
            maxLength={50}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Color</label>
          <div className={styles.colorGrid}>
            {FOLDER_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`${styles.colorOption} ${
                  selectedColor === color ? styles.colorSelected : ''
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedColor(color)}
                title={color}
              />
            ))}
          </div>
        </div>

        <div className={styles.actions}>
          <Button type="button" variant="secondary" block onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" block disabled={!name.trim() || mutation.isPending}>
            {mutation.isPending
              ? (isEdit ? 'Saving...' : 'Creating...')
              : (isEdit ? 'Save Changes' : 'Create Folder')}
          </Button>
        </div>

        {mutation.isError && (
          <div className={styles.error}>
            {(mutation.error as any)?.response?.data?.error || 'Failed to save folder'}
          </div>
        )}
      </form>
    </Modal>
  )
}
